import { useState, useRef } from "react";
import {
  Modal,
  ModalBody,
  CardTitle,
  Card,
  CardBody,
  Button,
  ModalFooter,
  Input,
  Col,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { Label } from "reactstrap/lib";
import { AddNewProgram } from "../../actions/superUserMutations";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { CardImg } from "reactstrap/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

const AddNewProgramModal = ({ isOpen, toggle }) => {
  const intl = useIntl();
  const [newProgram, setNewProgram] = useState({
    ProgramName: "",
    FortNoxNumberStudentLicense: "",
    FortNoxNumberSchoolLicense: "",
    CostCenter: "",
    PriceList: "A",
    ImageFile: null,
    IsAplProgram: false,
  });
  const inputRef = useRef(null);
  const [posting, setPosting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [file, setFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const queryClient = useQueryClient();

  const addNewProgram = useMutation({ mutationFn: AddNewProgram });

  const clearData = () => {
    setNewProgram({
      ProgramName: "",
      FortNoxNumberStudentLicense: "",
      FortNoxNumberSchoolLicense: "",
      CostCenter: "",
      PriceList: "A",
      ImageFile: null,
      IsAplProgram: false,
    });
    setPosting(false);
    setErrors([]);
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleAddProgram = () => {
    setPosting(true);

    const formData = new FormData();

    formData.append("ProgramName", newProgram.ProgramName);
    formData.append(
      "FortNoxNumberStudentLicense",
      newProgram.FortNoxNumberStudentLicense
    );
    formData.append(
      "FortNoxNumberSchoolLicense",
      newProgram.FortNoxNumberSchoolLicense
    );
    formData.append("CostCenter", newProgram.CostCenter);
    formData.append("PriceList", newProgram.PriceList);
    formData.append("ImageFile", newProgram.ImageFile);
    formData.append("IsAplProgram", newProgram.IsAplProgram);

    addNewProgram.mutate(formData, {
      onSuccess: (res) => {
        if (res && res.status > 199 && res.status < 300) {
          setPosting(false);
          clearData();
          toggle();
          queryClient.invalidateQueries({ queryKey: ["FetchAdminCourses"] });
          queryClient.invalidateQueries({ queryKey: ["FetchAdminCoursesSlim"] });
          queryClient.invalidateQueries({ queryKey: ["FetchFortnoxPrograms"] });
          queryClient.invalidateQueries({ queryKey: ["FetchAvailableProgramLicenses"] });
          toast.success(<FormattedMessage id="ProgramAdded" />);
        } else {
          setPosting(false);
          toast.warn(formatServiceError(intl, res.response.data));
        }
      },
      onError: (data) => {
        setPosting(false);
        toast.warn(formatServiceError(intl, data.response.data));
      },
    });
  };

  const handleValidateNewProgram = () => {
    let tempErrors = [];
    if (newProgram.ProgramName.length < 1) {
      tempErrors.push("ProgramName");
    } else {
      tempErrors = tempErrors.filter((item) => item !== "ProgramName");
    }
    if (!newProgram.IsAplProgram && newProgram.FortNoxNumberStudentLicense.length < 1) {
      tempErrors.push("FortNoxNumberStudentLicense");
    } else {
      tempErrors = tempErrors.filter(
        (item) => item !== "FortNoxNumberStudentLicense"
      );
    }
    if (newProgram.FortNoxNumberSchoolLicense.length < 1) {
      tempErrors.push("FortNoxNumberSchoolLicense");
    } else {
      tempErrors = tempErrors.filter(
        (item) => item !== "FortNoxNumberSchoolLicense"
      );
    }
    if (newProgram.CostCenter.length < 1) {
      tempErrors.push("CostCenter");
    } else {
      tempErrors = tempErrors.filter((item) => item !== "CostCenter");
    }
    if (newProgram.PriceList.length < 1) {
      tempErrors.push("PriceList");
    } else {
      tempErrors = tempErrors.filter((item) => item !== "PriceList");
    }

    if (file && validateImageFile(file)) {
      tempErrors.push("FileType");
    } else {
      tempErrors = tempErrors.filter((item) => item !== "FileType");
    }
    if (!newProgram.ImageFile && !file) {
      tempErrors.push("ImageFile");
    } else {
      tempErrors = tempErrors.filter((item) => item !== "ImageFile");
    }
    setErrors(tempErrors);
    if (tempErrors.length === 0) {
      handleAddProgram();
    }
  };

  const validateImageFile = (file) => {
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/svg+xml",
    ];

    if (!allowedTypes.includes(file.type)) return "FileType";

    return null;
  };

  const handleRemoveImageFile = () => {
    setFile(null);

    setNewProgram({ ...newProgram, ImageFile: null });
    setErrors((prev) => prev.filter((item) => item !== "FileType"));

    if (inputRef.current) inputRef.current.value = "";
  };

  const handleImageFile = (selectedFile) => {
    if (!selectedFile) return;

    setFile(selectedFile);

    const error = validateImageFile(selectedFile);

    if (!error) {
      setNewProgram({ ...newProgram, ImageFile: selectedFile });
      setErrors((prev) => prev.filter((item) => item !== "FileType"));
    } else {
      setNewProgram({ ...newProgram, ImageFile: null });
      setErrors((prev) => [
        ...prev.filter((item) => item !== "FileType"),
        error,
      ]);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()}>
      <ModalBody>
        <CardTitle>
          <FormattedMessage id="Add" />
        </CardTitle>
        <Card>
          <CardBody>
            <div className="form-check mb-3">
              <Input
                type="checkbox"
                className="form-check-input"
                id="IsAplProgram"
                checked={newProgram.IsAplProgram}
                onChange={(e) =>
                  setNewProgram({
                    ...newProgram,
                    IsAplProgram: e.target.checked,
                    FortNoxNumberStudentLicense: e.target.checked
                      ? ""
                      : newProgram.FortNoxNumberStudentLicense,
                  })
                }
              />
              <Label className="form-check-label" for="IsAplProgram">
                <FormattedMessage id="IsAplProgram" />
              </Label>
            </div>
            <Label for="ProgramName">
              <FormattedMessage id="ProgramName" />:
            </Label>
            {errors.includes("ProgramName") && (
              <Col className="text-danger">
                <FormattedMessage id="Views.Admin.CreateProgram.Error.RequireName" />
              </Col>
            )}
            <Input
              name="ProgramName"
              type="text"
              placeholder={intl.formatMessage({ id: "ProgramName" })}
              onChange={(data) =>
                setNewProgram({ ...newProgram, ProgramName: data.target.value })
              }
            ></Input>
            <Label for="FortNoxNumberStudentLicense">
              <FormattedMessage id="FortnoxNumberStudentLicense" />:
            </Label>
            {errors.includes("FortNoxNumberStudentLicense") && (
              <Col className="text-danger">
                <FormattedMessage id="YouHaveToEnterFortnoxNumberForStudentLicense" />
              </Col>
            )}
            <Input
              name="FortNoxNumberStudentLicense"
              type="text"
              placeholder={intl.formatMessage({ id: "FortnoxNumberForStudentLicense" })}
              disabled={newProgram.IsAplProgram}
              value={newProgram.FortNoxNumberStudentLicense}
              onChange={(data) =>
                setNewProgram({
                  ...newProgram,
                  FortNoxNumberStudentLicense: data.target.value,
                })
              }
            ></Input>
            <Label for="FortNoxNumberSchoolLicense">
              <FormattedMessage id="FortnoxNumberProgramLicense" />:
            </Label>
            {errors.includes("FortNoxNumberSchoolLicense") && (
              <Col className="text-danger">
                <FormattedMessage id="YouHaveToEnterFortnoxNumberForProgramLicense" />
              </Col>
            )}
            <Input
              name="FortNoxNumberSchoolLicense"
              type="text"
              placeholder={intl.formatMessage({ id: "FortnoxNumberSchoolLicense" })}
              onChange={(data) =>
                setNewProgram({
                  ...newProgram,
                  FortNoxNumberSchoolLicense: data.target.value,
                })
              }
            ></Input>
            <Label for="CostCenter">
              <FormattedMessage id="CostCenter" />:
            </Label>
            {errors.includes("CostCenter") && (
              <Col className="text-danger">
                <FormattedMessage id="YouHaveToEnterCostCenter" />
              </Col>
            )}
            <Input
              name="CostCenter"
              type="text"
              placeholder={intl.formatMessage({ id: "CostCenter" })}
              value={newProgram.CostCenter}
              onChange={(data) =>
                setNewProgram({ ...newProgram, CostCenter: data.target.value })
              }
            ></Input>
            <Label for="PriceList">
              <FormattedMessage id="PriceList" />:
            </Label>
            {errors.includes("PriceList") && (
              <Col className="text-danger">
                <FormattedMessage id="YouHaveToEnterPriceList" />
              </Col>
            )}
            <Input
              name="PriceList"
              type="text"
              defaultValue={newProgram.PriceList}
              onChange={(data) =>
                setNewProgram({ ...newProgram, PriceList: data.target.value })
              }
            ></Input>
            <Label for="LicenseImage">
              <FormattedMessage id="LicenseImage" />:
            </Label>
            <div className="d-flex align-items-center">
              <Input
                name="Upload Image"
                type="file"
                onChange={(data) => handleImageFile(data.target.files[0])}
                className="form-control mb-2"
                innerRef={inputRef}
              />
              {file && (
                <FontAwesomeIcon
                  icon="trash"
                  className="ms-2 mb-2"
                  style={{
                    cursor: "pointer",
                    transition: "color 0.2s",
                    color: "black",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "red")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "black")}
                  title={intl.formatMessage({ id: "RemoveImage" })}
                  onClick={handleRemoveImageFile}
                />
              )}
            </div>
            {errors.includes("ImageFile") && (
              <Col className="text-danger">
                <FormattedMessage id="ImageRequired" />
              </Col>
            )}
            {errors.includes("FileType") && (
              <Col className="text-danger">
                <FormattedMessage id="FileType" />
              </Col>
            )}
            {file && !errors.includes("FileType") && (
              <>
                <Button
                  outline
                  color="primary"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  className="float-end mb-2"
                >
                  <FontAwesomeIcon
                    icon={showPreview ? "eye" : "eye-slash"}
                    className="me-1"
                  />
                  <FormattedMessage
                    id={showPreview ? "HideImage" : "ShowImage"}
                  />
                </Button>
                {showPreview && (
                  <CardImg
                    src={URL.createObjectURL(file)}
                    alt="Preview image"
                    width="100%"
                    className="mt-2"
                  />
                )}
              </>
            )}
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button
          outline
          color="secondary"
          onClick={() => {
            clearData();
            toggle();
          }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          outline
          color="primary"
          disabled={posting}
          onClick={() => handleValidateNewProgram()}
        >
          {posting ? (
            <FormattedMessage id="AddingDotDotDot" />
          ) : (
            <FormattedMessage id="Add" />
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddNewProgramModal;
